import { gql, useMutation, useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import {
  ArtistMembershipFavoriteMembersFragmentDoc,
  ArtistPatronFavoriteMemberFragmentDoc,
  FavoriteMembersSelectWithPatronQuery,
  FavoriteMembersSelectWithPatronQueryVariables,
  UpdateMyFavoriteMemberMutation,
  UpdateMyFavoriteMemberMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, generatePath, routerPages } =
    useRouter<{ artistId: string; productId: string }>()
  const { showError } = useMessage()
  const artistId = router.query.artistId ?? config.artistId

  const [selectedFavoriteMember, setSelectedFavoriteMember] = useState<string>()
  const [nextChangeableDate, setNextChangeableDate] = useState<string>('')

  const { data, loading } = useQuery<
    FavoriteMembersSelectWithPatronQuery,
    FavoriteMembersSelectWithPatronQueryVariables
  >(QUERY, { variables: { artistId } })

  const [updateMyFavoriteMember, { loading: updatingMember }] = useMutation<
    UpdateMyFavoriteMemberMutation,
    UpdateMyFavoriteMemberMutationVariables
  >(MUTATION, {
    variables: {
      inputs: { artistId, favoriteMemberId: selectedFavoriteMember ?? '' },
    },
  })

  useEffect(() => {
    // 初期表示でQUERYの結果が存在する場合はそれをセットする
    setSelectedFavoriteMember(data?.artist?.patron?.favoriteMember?.id)

    if (data?.artist?.patron?.favoriteMember?.createdAt) {
      const nextChangeableDate = dayjs(
        data?.artist?.patron?.favoriteMember?.createdAt
      ).add(91, 'day')
      if (dayjs(nextChangeableDate).isAfter(dayjs(), 'day')) {
        // 初期表示でpatron.favoriteMember.createdAtから91日経過しているかを判定
        // 経過していない場合は91日後の日付をnextChangeableDateとしてセットする
        setNextChangeableDate(dayjs(nextChangeableDate).format('YYYY/MM/DD'))
      }
    }
  }, [data])

  return {
    state: {
      artist: data?.artist,
      artistId,
      nextChangeableDate,
      selectedFavoriteMember,
      initialSelectedFavoriteMember: data?.artist?.patron?.favoriteMember?.id,
      isPatron: !!data?.artist?.patron,
      favoriteMembers: data?.artist?.favoriteMembers,
      initialLoading: loading,
      updatingMember,
    },
    actions: {
      async onConfirm() {
        await updateMyFavoriteMember()
          .then(() => {
            router.push(generatePath(routerPages.MyProfile))
          })
          .catch(error => {
            showError(error)
          })
      },
      onSelectFavoriteMember(favoriteMemberId: string) {
        setSelectedFavoriteMember(favoriteMemberId)
      },
    },
  }
})

const QUERY = gql`
  query FavoriteMembersSelectWithPatron($artistId: ID) {
    artist(id: $artistId) {
      displayName
      avatar {
        uri
      }
      ...ArtistMembershipFavoriteMembers
      ...ArtistPatronFavoriteMember
    }
  }
  ${ArtistMembershipFavoriteMembersFragmentDoc}
  ${ArtistPatronFavoriteMemberFragmentDoc}
`
const MUTATION = gql`
  mutation updateMyFavoriteMember($inputs: UpdateMyFavoriteMemberInput!) {
    updateMyFavoriteMember(inputs: $inputs) {
      id
      name
      avatar {
        uri
      }
    }
  }
`
